/**
 * SLAConfigPage — `/settings/sla-config`.
 *
 * Define delivery and complaint resolution targets. SLA rows are stored in
 * the platform_setting table (service-audit) as JSON values under
 * `sla_config.<priority>.<category>` style keys. Falls back to seeded
 * defaults when the endpoint is unreachable.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Timer } from 'lucide-react';

import { listPlatformSettings, upsertPlatformSetting } from '../../lib/api/settings';
import { PageHeader } from '../shared/StaffUI';

interface SLARow {
  readonly id: string;
  readonly category: 'DELIVERY' | 'COMPLAINT' | 'GOV_SERVICE';
  readonly priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  readonly description: string;
  readonly responseHours: number;
  readonly resolutionHours: number;
}

const SLA_SETTING_KEY = 'sla_config';

const SEED: readonly SLARow[] = [
  { id: 's1', category: 'COMPLAINT', priority: 'CRITICAL', description: 'Lost / damaged high-value parcel', responseHours: 2, resolutionHours: 24 },
  { id: 's2', category: 'COMPLAINT', priority: 'HIGH', description: 'Failed delivery dispute', responseHours: 4, resolutionHours: 48 },
  { id: 's3', category: 'COMPLAINT', priority: 'MEDIUM', description: 'Tracking discrepancy', responseHours: 8, resolutionHours: 72 },
  { id: 's4', category: 'DELIVERY', priority: 'HIGH', description: 'EMS Express — same city', responseHours: 0, resolutionHours: 6 },
  { id: 's5', category: 'DELIVERY', priority: 'MEDIUM', description: 'Standard mail — national', responseHours: 0, resolutionHours: 72 },
  { id: 's6', category: 'GOV_SERVICE', priority: 'HIGH', description: 'NIN enrolment', responseHours: 1, resolutionHours: 24 },
];

export function SLAConfigPage(): JSX.Element {
  const qc = useQueryClient();
  const [localRows, setLocalRows] = useState<readonly SLARow[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState('');

  const settings = useQuery({
    queryKey: ['platform-settings'],
    queryFn: () => listPlatformSettings(),
    retry: false,
    staleTime: 30_000,
  });

  const saveMutation = useMutation({
    mutationFn: (rows: readonly SLARow[]) =>
      upsertPlatformSetting(SLA_SETTING_KEY, rows, 'SLA configuration'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['platform-settings'] });
      setDirty(false);
      setSaveError('');
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    },
  });

  // Derive rows: prefer localRows (unsaved edits), then server value, then seed.
  function getServerRows(): readonly SLARow[] {
    const setting = settings.data?.items.find((s) => s.key === SLA_SETTING_KEY);
    if (setting && Array.isArray(setting.value)) return setting.value as readonly SLARow[];
    return SEED;
  }

  const rows = localRows ?? getServerRows();

  function update(id: string, patch: Partial<SLARow>): void {
    const next = rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
    setLocalRows(next);
    setDirty(true);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="SLA configuration"
        description="Define delivery and complaint resolution targets by priority."
      />

      {settings.isError ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
          <p className="font-semibold">Service-audit unreachable</p>
          <p>SLA config is using seeded defaults. Changes will be persisted once the service is available.</p>
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Category</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Priority</th>
              <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Description</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Response (h)</th>
              <th className="text-right px-4 py-2 text-[10px] uppercase tracking-wider text-muted">Resolution (h)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs">
                  <span className="inline-flex items-center gap-1 text-xs">
                    <Timer className="w-3 h-3" /> {r.category}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs">{r.priority}</td>
                <td className="px-4 py-2 text-xs text-gray-700">{r.description}</td>
                <td className="px-4 py-1.5">
                  <input
                    type="number"
                    value={r.responseHours}
                    aria-label={`Response ${r.id}`}
                    onChange={(e) => update(r.id, { responseHours: Number(e.target.value) })}
                    className="w-20 px-2 py-1 border border-border rounded text-xs text-right"
                  />
                </td>
                <td className="px-4 py-1.5">
                  <input
                    type="number"
                    value={r.resolutionHours}
                    aria-label={`Resolution ${r.id}`}
                    onChange={(e) => update(r.id, { resolutionHours: Number(e.target.value) })}
                    className="w-20 px-2 py-1 border border-border rounded text-xs text-right"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <div>
          {saveError ? <p className="text-xs text-red-600">{saveError}</p> : null}
        </div>
        <button
          type="button"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate(rows)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> {saveMutation.isPending ? 'Saving…' : 'Save SLA config'}
        </button>
      </div>
    </div>
  );
}
